import type { PortalActivity, PortalSummaryTile, SalesBreakdown } from "@khazzin/api-client";
import type { Locale, Translate } from "@khazzin/i18n";
import { formatDateTime } from "@khazzin/i18n/dates";
import { formatMoney } from "@khazzin/i18n/money";
import {
	currenciesIn,
	datesOf,
	formatCount,
	formatDay,
	Loaded,
	trendRows,
} from "@khazzin/ui/analytics";
import { BarSeries, ChartCard, type ChartData, type ChartRow, LineTrend } from "@khazzin/ui/charts";
import { type DateRange, presetRange, todayIn } from "@khazzin/ui/patterns";
import { EmptyState, ErrorState, KpiTile, PageHeader } from "@khazzin/ui/patterns/page";
import { SkeletonText } from "@khazzin/ui/primitives";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { useApi } from "../../api";
import { useLocale } from "../../app/shell/locale";
import { NotFound } from "../../app/shell/Shell";
import { useStoreScope } from "../../app/shell/scope";
import { levelOf } from "../../app/shell/sections";
import { usePortalSales } from "../analytics/AnalyticsPage";

/**
 * The Store Portal's home (EP10-S13, DA-10-02): the KpiTile row — Orders
 * today, Out for delivery, Stock alerts, Current balance, Last cashout — then
 * orders per day, revenue and the top items over the last 30 days, and the
 * recent activity feed.
 *
 * **The server decides which tiles exist.** A tile whose section the grid
 * hides is not in `/portal/summary` at all, so the screen draws what it is
 * given and never guesses. The charts are Sales & Revenue's and show only
 * with the Analytics section. Tiles re-read on the stream's order and stock
 * hints (the shell's topics) and every 60 s otherwise.
 */

export function dashboardKey(storeId: string) {
	return ["portal", storeId, "dashboard"] as const;
}

/** The tiles' poll behind the stream (`10/05` §8). */
export const SUMMARY_POLL_MS = 60_000;

/** Where each tile drills to. */
const TILE_LINKS: Record<PortalSummaryTile["key"], string> = {
	orders_today: "/orders",
	out_for_delivery: "/orders",
	stock_alerts: "/inventory/low-stock",
	current_balance: "/finance",
	last_cashout: "/finance?tab=payouts",
};

/** A tile's figure as it reads. */
export function tileValue(tile: PortalSummaryTile, t: Translate, locale: Locale): string {
	if (tile.count !== undefined && tile.count !== null) {
		return tile.critical
			? t("portal.dashboard.withCritical", {
					count: formatCount(tile.count, locale),
					critical: formatCount(tile.critical, locale),
				})
			: formatCount(tile.count, locale);
	}
	if (tile.amountMinor === undefined || tile.amountMinor === null || !tile.currency) {
		return t("portal.dashboard.none");
	}
	return formatMoney({ amountMinor: tile.amountMinor, currency: tile.currency }, locale);
}

/** A feed line's words. */
export function activityText(a: PortalActivity, t: Translate, locale: Locale): string {
	switch (a.kind) {
		case "order":
			return t("portal.dashboard.activity.order", {
				orderNo: a.orderNo ?? "",
				status: t(`orders.status.${a.status ?? ""}`),
			});
		case "receipt":
			return t("portal.dashboard.activity.receipt", {
				reference: a.reference ?? a.asnId ?? "",
				units: formatCount(a.units ?? 0, locale),
			});
		default:
			return t("portal.dashboard.activity.payout", {
				reference: a.reference ?? "",
				amount:
					a.amountMinor === undefined || a.amountMinor === null || !a.currency
						? "—"
						: formatMoney({ amountMinor: a.amountMinor, currency: a.currency }, locale),
			});
	}
}

/** Where a feed line opens. */
export function activityLink(a: PortalActivity): string {
	switch (a.kind) {
		case "order":
			return `/orders/${a.orderId ?? ""}`;
		case "receipt":
			return "/inventory/receiving";
		default:
			return "/finance?tab=payouts";
	}
}

/** Orders per day over every currency's rows: a count, so currencies add up. */
export function ordersPerDay(range: DateRange, trend: SalesBreakdown | undefined): ChartRow[] {
	const byDay = new Map<string, number>();
	for (const row of trend?.rows ?? []) {
		if (row.date !== null) {
			byDay.set(row.date, (byDay.get(row.date) ?? 0) + row.orders);
		}
	}
	return datesOf(range).map((date) => ({ date, current: byDay.get(date) ?? 0 }));
}

export function DashboardPage() {
	const { t, locale } = useLocale();
	const { store, context } = useStoreScope();
	const api = useApi();
	const timeZone = context?.preferences?.timezone ?? "Asia/Beirut";
	const storeId = store?.storeId ?? "";
	const open = levelOf(store, "dashboard") !== "none";
	const charts = open && levelOf(store, "analytics") !== "none";
	const range = presetRange("last30", todayIn(timeZone));

	const summary = useQuery({
		queryKey: [...dashboardKey(storeId), "summary"],
		queryFn: ({ signal }) => api.getPortalSummary({ signal }),
		enabled: storeId !== "" && open,
		refetchInterval: SUMMARY_POLL_MS,
	});
	const trend = usePortalSales(storeId, "trend", range, { enabled: charts });
	const top = usePortalSales(storeId, "top-skus", range, { limit: 5, enabled: charts });

	if (store !== null && !open) {
		return <NotFound />;
	}
	const day = (value: string) => formatDay(value, locale);

	return (
		<div className="flex flex-col gap-4">
			<PageHeader title={t("portal.dashboard.title")} subtitle={t("portal.dashboard.subtitle")} />
			{summary.isPending ? (
				<SkeletonText lines={3} />
			) : summary.isError ? (
				<ErrorState error={summary.error} onRetry={() => void summary.refetch()} t={t} />
			) : (
				<section
					aria-label={t("portal.dashboard.tiles")}
					className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5"
				>
					{summary.data.tiles.map((tile) => (
						<KpiTile
							key={tile.key}
							label={t(`portal.dashboard.tile.${tile.key}`)}
							value={tileValue(tile, t, locale)}
							status="ready"
							href={TILE_LINKS[tile.key]}
							t={t}
						/>
					))}
				</section>
			)}
			{charts && (
				<div className="grid gap-4 lg:grid-cols-2">
					<Loaded query={trend} t={t}>
						<OrdersChart range={range} trend={trend.data} t={t} locale={locale} day={day} />
					</Loaded>
					<Loaded query={trend} t={t}>
						{currenciesIn(trend.data).map((currency) => {
							const data: ChartData = {
								x: { key: "date", label: t("portal.dashboard.day"), format: day },
								series: [{ key: "current", label: t("portal.dashboard.revenue"), color: 1 }],
								rows: trendRows(range, trend.data, currency, "revenueMinor", false),
								format: (amountMinor) => formatMoney({ amountMinor, currency }, locale),
							};
							return (
								<ChartCard
									key={currency}
									title={t("portal.dashboard.revenueTitle", { currency })}
									summary={t("portal.dashboard.last30")}
									data={data}
									t={t}
								>
									<LineTrend {...data} />
								</ChartCard>
							);
						})}
					</Loaded>
					<Loaded query={top} t={t}>
						<section aria-labelledby="top-items" className="flex flex-col gap-2 card p-4">
							<h2 id="top-items" className="text-[14px] font-bold text-fg">
								{t("portal.dashboard.topItems")}
							</h2>
							{(top.data?.rows ?? []).length === 0 ? (
								<p className="text-[12.5px] text-fg-muted">{t("portal.dashboard.noSales")}</p>
							) : (
								<ol className="flex flex-col gap-1.5">
									{(top.data?.rows ?? []).map((row) => (
										<li
											key={`${row.skuId}|${row.currency}`}
											className="flex items-center justify-between gap-3 text-[13px]"
										>
											{/* The SKU's code: `usePortalSales` names it (ADJ-M03-4). */}
											<span className="min-w-0 truncate font-mono">{row.skuId}</span>
											<span className="shrink-0 text-fg-muted">
												{t("portal.dashboard.units", { units: formatCount(row.units, locale) })}
												{row.currency
													? ` · ${formatMoney({ amountMinor: row.revenueMinor, currency: row.currency }, locale)}`
													: ""}
											</span>
										</li>
									))}
								</ol>
							)}
						</section>
					</Loaded>
				</div>
			)}
			<section aria-labelledby="activity" className="flex flex-col gap-2 card p-4">
				<h2 id="activity" className="text-[14px] font-bold text-fg">
					{t("portal.dashboard.activity.title")}
				</h2>
				{summary.isSuccess && summary.data.activity.length === 0 ? (
					<EmptyState title={t("portal.dashboard.activity.empty")} />
				) : (
					<ul className="flex flex-col divide-y divide-border">
						{(summary.data?.activity ?? []).map((a) => (
							<li key={`${a.kind}|${a.orderId ?? a.asnId ?? a.reference}|${a.at}`}>
								<Link
									to={activityLink(a)}
									className="flex min-h-12 items-center justify-between gap-3 py-2 text-[13px] text-fg hover:text-brand"
								>
									<span className="min-w-0">{activityText(a, t, locale)}</span>
									<span className="shrink-0 text-[12px] text-fg-muted">
										{formatDateTime(a.at, { locale, timeZone })}
									</span>
								</Link>
							</li>
						))}
					</ul>
				)}
			</section>
		</div>
	);
}

function OrdersChart({
	range,
	trend,
	t,
	locale,
	day,
}: {
	range: DateRange;
	trend: SalesBreakdown | undefined;
	t: Translate;
	locale: Locale;
	day: (value: string) => string;
}) {
	const data: ChartData = {
		x: { key: "date", label: t("portal.dashboard.day"), format: day },
		series: [{ key: "current", label: t("portal.dashboard.orders"), color: 1 }],
		rows: ordersPerDay(range, trend),
		format: (value) => formatCount(value, locale),
	};
	return (
		<ChartCard
			title={t("portal.dashboard.ordersPerDay")}
			summary={t("portal.dashboard.last30")}
			data={data}
			t={t}
		>
			<BarSeries {...data} />
		</ChartCard>
	);
}
